import { HttpService } from "../../../app/services/http/http.service"
import { StudentHelperEvent } from "../models/StudentHelperEvent"
import {
  StudentHelperAssignmentPreparationStatus,
  StudentHelperAssignmentPreparationStatusDTO,
} from "../models/StudentHelperAssignmentPreparationStatus"
import { StudentHelperPoller } from "./StudentHelperPoller"

/**
 * Service for managing student helper (instant helper) functionality
 */
export class StudentHelperService {
  constructor(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    private environment: any,
    private HttpService: HttpService,
    private studentHelperPoller: StudentHelperPoller,
  ) {
    "ngInject"
  }

  private get baseUrl(): string {
    return `${this.environment.serverUrlBase}/v1/instant-helper`
  }

  /**
   * Gets the preparation task status for an assignment.
   *
   * Used to get the preparationTaskId.
   * More expensive than getPreparationTaskStatus.
   */
  async getPreparationTaskStatusForAssignment(
    assignmentId: string,
  ): Promise<StudentHelperAssignmentPreparationStatus> {
    const res: StudentHelperAssignmentPreparationStatusDTO =
      await this.HttpService.authGet(
        `${this.baseUrl}/assignment/${assignmentId}/preparation-task-status`,
      )
    return StudentHelperAssignmentPreparationStatus.fromDTO(res)
  }

  /**
   * Gets the preparation task status for a specific preparation task.
   *
   * Less expensive version of getPreparationTaskStatusForAssignment since it
   * doesn't need to search for preparationTaskId.
   */
  async getPreparationTaskStatus(
    assignmentId: string,
    preparationTaskId: string,
  ): Promise<StudentHelperAssignmentPreparationStatus> {
    const res: StudentHelperAssignmentPreparationStatusDTO =
      await this.HttpService.authGet(
        `${this.baseUrl}/assignment/${assignmentId}/prepare-assignment/preparation-task/${preparationTaskId}/status`,
      )
    return StudentHelperAssignmentPreparationStatus.fromDTO(res)
  }

  async pollPreparationTaskStatus(
    assignmentId: string,
    preparationTaskId: string,
  ): Promise<StudentHelperAssignmentPreparationStatus> {
    return this.studentHelperPoller.poll(() =>
      this.getPreparationTaskStatus(assignmentId, preparationTaskId),
    )
  }

  /**
   * Prepares an assignment for instant helper
   */
  async prepareAssignment(
    assignmentId: string,
    options: { forced?: boolean },
  ): Promise<StudentHelperAssignmentPreparationStatus> {
    const res: StudentHelperAssignmentPreparationStatusDTO =
      await this.HttpService.authPost(
        `${this.baseUrl}/assignment/${assignmentId}/prepare-assignment`,
        options,
      )
    return StudentHelperAssignmentPreparationStatus.fromDTO(res)
  }

  /**
   * Prepares an assignment for a specific roster
   */
  async prepareAssignmentRoster(
    assignmentId: string,
    rosterId: string,
    options: { forced?: boolean },
  ): Promise<StudentHelperAssignmentPreparationStatus> {
    const res: StudentHelperAssignmentPreparationStatusDTO =
      await this.HttpService.authPost(
        `${this.baseUrl}/assignment/${assignmentId}/roster/${rosterId}/prepare-assignment`,
        options,
      )
    return StudentHelperAssignmentPreparationStatus.fromDTO(res)
  }

  /**
   * Gets the preparation task status for an assignment roster
   */
  async getPreparationTaskStatusForAssignmentRoster(
    assignmentId: string,
    rosterId: string,
    preparationTaskId: string,
  ): Promise<StudentHelperAssignmentPreparationStatus> {
    const res: StudentHelperAssignmentPreparationStatusDTO =
      await this.HttpService.authGet(
        `${this.baseUrl}/assignment/${assignmentId}/roster/${rosterId}/prepare-assignment/preparation-task/${preparationTaskId}/status`,
      )
    return StudentHelperAssignmentPreparationStatus.fromDTO(res)
  }

  /**
   * Requests helper for a specific question
   */
  async requestHelper(
    assignmentId: string,
    questionId: string,
    questionWorkId: string,
    options: { workThumbnailUrl: string; launchedAt?: Date },
  ): Promise<string> {
    const res: { session_id: string } = await this.HttpService.authPost(
      `${this.baseUrl}/${assignmentId}/${questionId}/${questionWorkId}/request-helper`,
      options,
    )
    return res.session_id
  }

  /**
   * Adds events to a helper session
   */
  async addEvents(
    assignmentId: string,
    questionId: string,
    questionWorkId: string,
    sessionId: string,
    events: Array<StudentHelperEvent>,
  ): Promise<string> {
    const res: { response_action: string } = await this.HttpService.authPost(
      `${this.baseUrl}/${assignmentId}/${questionId}/${questionWorkId}/${sessionId}/add-events`,
      { events: events.map((event) => event.toDTO()) },
    )
    return res.response_action
  }

  /**
   * Closes a helper session
   */
  closeSession(
    assignmentId: string,
    questionId: string,
    questionWorkId: string,
    sessionId: string,
  ): Promise<void> {
    return this.HttpService.authPost(
      `${this.baseUrl}/${assignmentId}/${questionId}/${questionWorkId}/${sessionId}/close-session`,
    )
  }

  /**
   * Deactivates a teacher and closes all active sessions
   */
  deactivateTeacher(teacherId: string): Promise<void> {
    return this.HttpService.authPut(
      `${this.baseUrl}/deactivate/teacher/${teacherId}`,
    )
  }

  /**
   * Deactivates an organization and closes all active sessions
   */
  deactivateOrganization(organizationId: string): Promise<void> {
    return this.HttpService.authPut(
      `${this.baseUrl}/deactivate/organization/${organizationId}`,
    )
  }
}
